import { useMemo, useState } from "react";
import Shell from "../components/Shell";
import { useKatalog, useKatalogAendern, useKatalogAnlegen, eur, MONAT_KURZ } from "../hooks/useVertraege";
import type { KatalogPatch } from "../hooks/useVertraege";
import { GARTEN_TYPEN } from "../lib/wizard";
import { einheitLabel, kategorieLabel } from "../lib/labels";
import type { KatalogEintrag } from "../lib/wizard";
import { zahl } from "../lib/zahl";

const EINHEITEN = ["qm", "lfm", "stueck", "std", "pauschal", "anfahrt", "fuhre", "cbm"];
const KATEGORIEN = [
  "rasen", "hecke", "beet", "baum", "unkraut", "laub", "duengung",
  "bewaesserung", "entsorgung", "maschine", "anfahrt", "sonstiges",
];

/**
 * Leistungskatalog pflegen — die Preisliste hinter jedem Vertrag.
 *
 * Hier stehen die Sätze, mit denen der Wizard rechnet (Heckenschnitt 4,50 €/lfm)
 * und die Zuordnung „passt zu Gartentyp", aus der die Vorschläge entstehen.
 * Bewusst kein Löschen: bestehende Verträge verweisen per leistung_id auf diese
 * Zeilen und verlören sonst ihren Titel. Stillgelegtes wird nicht mehr
 * vorgeschlagen, bleibt aber lesbar.
 */
export default function KatalogPage() {
  const { data: katalog = [], isLoading } = useKatalog(true);
  const anlegen = useKatalogAnlegen();
  const [offen, setOffen] = useState<string | null>(null);
  const [suche, setSuche] = useState("");
  const [zeigeStillgelegt, setZeigeStillgelegt] = useState(false);

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return katalog
      .filter(k => zeigeStillgelegt || k.aktiv !== false)
      .filter(k => !q || (k.titel + " " + k.kategorie + " " + (k.garten_typen || []).join(" ")).toLowerCase().includes(q));
  }, [katalog, suche, zeigeStillgelegt]);

  const gruppen = useMemo(() => {
    const m = new Map<string, KatalogEintrag[]>();
    for (const k of gefiltert) m.set(k.kategorie, [...(m.get(k.kategorie) || []), k]);
    return [...m.entries()];
  }, [gefiltert]);

  const stillgelegt = katalog.filter(k => k.aktiv === false).length;

  async function neu() {
    const e = await anlegen.mutateAsync({
      titel: "Neue Leistung", kategorie: "sonstiges", einheit: "stueck",
      vk_netto: 0, kosten_netto: 0, zeit_min_je_einheit: 0, lohnanteil_prozent: 100,
    }) as unknown as KatalogEintrag;
    setSuche("");
    setOffen(e?.id ?? null);
  }

  return (
    <Shell
      title="Leistungskatalog"
      subtitle={`${katalog.filter(k => k.aktiv !== false).length} Leistungen · Sätze und Gartenzuordnung`}
      action={<button className="pf-btn" onClick={neu} disabled={anlegen.isPending}>＋ Neue Leistung</button>}
    >
      <input
        className="pf-input" placeholder="Suchen (Titel, Kategorie, Gartentyp)…"
        value={suche} onChange={e => setSuche(e.target.value)}
      />
      {stillgelegt > 0 && (
        <button
          className="pf-linkbtn" style={{ marginTop: 10, fontSize: 13 }}
          onClick={() => setZeigeStillgelegt(v => !v)}
        >
          {zeigeStillgelegt ? "Stillgelegte ausblenden" : `${stillgelegt} stillgelegte anzeigen`}
        </button>
      )}

      {isLoading ? (
        <div className="pf-empty">Lade…</div>
      ) : gruppen.length === 0 ? (
        <div className="pf-empty">Keine Leistung gefunden.</div>
      ) : (
        gruppen.map(([kategorie, eintraege]) => (
          <div key={kategorie}>
            <div className="pf-seclabel">{kategorieLabel(kategorie)}</div>
            {eintraege.map(k => (
              <KatalogKarte
                /* key wechselt beim Auf-/Zuklappen: die Karte wird neu
                   aufgebaut und ein verworfener Entwurf ist wirklich weg. */
                key={k.id + (offen === k.id ? ":offen" : "")} k={k}
                offen={offen === k.id}
                onToggle={() => setOffen(offen === k.id ? null : k.id)}
              />
            ))}
          </div>
        ))
      )}
    </Shell>
  );
}

function KatalogKarte({ k, offen, onToggle }: { k: KatalogEintrag; offen: boolean; onToggle: () => void }) {
  const aendern = useKatalogAendern();
  const still = k.aktiv === false;

  // Der Entwurf lebt nur solange die Karte offen ist — `key` unten setzt ihn
  // beim Öffnen frisch aus den Serverwerten, damit ein Abbruch nichts hinterlässt.
  const [e, setE] = useState<KatalogPatch>({});
  const wert = <K extends keyof KatalogEintrag>(feld: K): KatalogEintrag[K] =>
    (feld in e ? (e as Record<string, unknown>)[feld as string] : k[feld]) as KatalogEintrag[K];
  const setzen = (patch: KatalogPatch) => setE(v => ({ ...v, ...patch }));
  const geaendert = Object.keys(e).length > 0;

  const monate = (wert("standard_monate") || []) as number[];
  const typen = (wert("garten_typen") || []) as string[];
  const vk = Number(wert("vk_netto") ?? 0);
  const kosten = Number(wert("kosten_netto") ?? 0);
  const marge = vk > 0 ? Math.round(((vk - kosten) / vk) * 100) : null;

  async function speichern() {
    if (!geaendert) return;
    await aendern.mutateAsync({ id: k.id, body: e });
    setE({});
  }

  if (!offen) {
    return (
      <div className="pf-row" onClick={onToggle} style={still ? { opacity: 0.72 } : undefined}>
        <div className="pf-row-main">
          <div style={{ fontWeight: 700 }}>{k.titel}{still ? " · stillgelegt" : ""}</div>
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 2 }}>
            {(k.garten_typen || []).length
              ? `passt zu ${(k.garten_typen || []).join(", ")}`
              : "wird nicht automatisch vorgeschlagen"}
            {(k.standard_monate || []).length
              ? ` · ${k.standard_monate.map(m => MONAT_KURZ[m - 1]).join(" ")}`
              : ""}
          </div>
        </div>
        <div className="pf-row-side">
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700 }}>{zahl(k.vk_netto, 2)} €/{einheitLabel(k.einheit)}</div>
            <div style={{ fontSize: 12, color: "var(--pf-ink-3)" }}>Kosten {zahl(k.kosten_netto, 2)} €</div>
          </div>
          <span className="pf-pill">Ändern</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pf-card" style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>{k.titel}</div>
        <button className="pf-linkbtn" onClick={onToggle}>Schließen</button>
      </div>

      <label className="pf-field"><span>Titel</span>
        <input className="pf-input" value={String(wert("titel") ?? "")}
          onChange={ev => setzen({ titel: ev.target.value })} />
      </label>

      <div className="pf-grid-2">
        <label className="pf-field"><span>Kategorie</span>
          <select className="pf-input" value={String(wert("kategorie") ?? "sonstiges")}
            onChange={ev => setzen({ kategorie: ev.target.value })}>
            {[...new Set([...KATEGORIEN, k.kategorie])].map(c => <option key={c} value={c}>{kategorieLabel(c)}</option>)}
          </select>
        </label>
        <label className="pf-field"><span>Einheit (danach wird gerechnet)</span>
          <select className="pf-input" value={String(wert("einheit") ?? "stueck")}
            onChange={ev => setzen({ einheit: ev.target.value })}>
            {[...new Set([...EINHEITEN, k.einheit])].map(c => <option key={c} value={c}>{einheitLabel(c)}</option>)}
          </select>
        </label>
        <label className="pf-field"><span>Preis € je Einheit (netto)</span>
          <input className="pf-input" type="number" inputMode="decimal" step="0.01"
            value={String(wert("vk_netto") ?? "")}
            onChange={ev => setzen({ vk_netto: Number(ev.target.value) || 0 })} />
        </label>
        <label className="pf-field"><span>Eigene Kosten € je Einheit</span>
          <input className="pf-input" type="number" inputMode="decimal" step="0.01"
            value={String(wert("kosten_netto") ?? "")}
            onChange={ev => setzen({ kosten_netto: Number(ev.target.value) || 0 })} />
        </label>
        <label className="pf-field"><span>Minuten je Einheit</span>
          <input className="pf-input" type="number" inputMode="decimal" step="0.01"
            value={String(wert("zeit_min_je_einheit") ?? "")}
            onChange={ev => setzen({ zeit_min_je_einheit: Number(ev.target.value) || 0 })} />
        </label>
        <label className="pf-field"><span>Lohnanteil % (§ 35a)</span>
          <input className="pf-input" type="number" inputMode="decimal" step="1"
            value={String(wert("lohnanteil_prozent") ?? "")}
            onChange={ev => setzen({ lohnanteil_prozent: Number(ev.target.value) || 0 })} />
        </label>
      </div>

      <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", margin: "2px 0 14px" }}>
        {marge === null
          ? "Ohne Preis rechnet der Vertrag mit 0 €."
          : `Deckungsbeitrag ${zahl(vk - kosten, 2)} € je ${einheitLabel(String(wert("einheit")))} (${marge} %).`}
      </div>

      <div className="pf-field"><span>Passt zu Gartentyp — daraus entstehen die Vorschläge im Wizard</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
          {GARTEN_TYPEN.filter(t => t.typ !== "Sonstiges").map(t => {
            const an = typen.includes(t.typ);
            return (
              <button key={t.typ} className={"pf-pill"} style={{
                cursor: "pointer", border: `1px solid ${an ? "var(--pf-accent)" : "var(--pf-line)"}`,
                background: an ? "var(--pf-accent-soft)" : "transparent",
                color: an ? "var(--pf-accent)" : "var(--pf-ink-3)", padding: "7px 12px", minHeight: 38,
              }} onClick={() => setzen({
                garten_typen: an ? typen.filter(x => x !== t.typ) : [...typen, t.typ],
              })}>{t.typ}</button>
            );
          })}
        </div>
        {typen.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--pf-ink-3)", marginTop: 6 }}>
            Ohne Zuordnung erscheint die Leistung nur über die Suche — richtig für
            Anfahrt, Arbeitsstunden und Entsorgung.
          </div>
        )}
      </div>

      {/* Mig 187: zwei Punkte im Monat nur, wo es Sinn ergibt (Benjamin,
          28.09.2026 — beim Mähen ja, beim Heckenschnitt nicht). Gilt für die
          Pflege-App und die Angebotsseite des Kunden. */}
      <label className="pf-field" style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
        <input type="checkbox" style={{ width: 20, height: 20, accentColor: "var(--pf-accent)" }}
          checked={Number(wert("max_je_monat") ?? 1) >= 2}
          onChange={ev => setzen({ max_je_monat: ev.target.checked ? 2 : 1 })} />
        <span style={{ margin: 0, fontSize: 13.5, color: "var(--pf-ink-2)" }}>
          Bis zu zweimal im Monat möglich (Zwei-Wochen-Rhythmus, z. B. Rasenmähen)
        </span>
      </label>

      <div className="pf-field"><span>Standardmonate — so oft im Jahr wird die Leistung geplant</span>
        <div className="pf-monate">
          {MONAT_KURZ.map((m, i) => {
            const an = monate.includes(i + 1);
            return (
              <button key={m} className={"pf-monat" + (an ? " is-on" : "")}
                onClick={() => setzen({
                  standard_monate: an
                    ? monate.filter(x => x !== i + 1)
                    : [...monate, i + 1].sort((a, b) => a - b),
                })}>{m}</button>
            );
          })}
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginBottom: 14 }}>
        Beispiel: 100 {einheitLabel(String(wert("einheit")))} × {zahl(vk, 2)} € ×{" "}
        {Math.max(1, monate.length)} Einsätze = <strong>{eur(vk * 100 * Math.max(1, monate.length))}</strong> im Jahr.
      </div>

      <div className="pf-actions" style={{ marginTop: 0 }}>
        <button className="pf-btn" onClick={speichern} disabled={!geaendert || aendern.isPending}>
          {aendern.isPending ? "Speichere…" : geaendert ? "Speichern" : "Nichts geändert"}
        </button>
        <button className="pf-btn is-ghost" onClick={() => setE({})} disabled={!geaendert}>Verwerfen</button>
        <button
          className="pf-btn is-ghost" style={{ marginLeft: "auto", color: still ? "var(--pf-accent)" : "var(--pf-red)" }}
          onClick={() => aendern.mutate({ id: k.id, body: { aktiv: still } })}
        >
          {still ? "Wieder aktivieren" : "Stilllegen"}
        </button>
      </div>
    </div>
  );
}
